import React, {
  useEffect,
  useLayoutEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import Chart, { loaderTypes } from "../../../Components/Chart/Chart";
import useChart from "../../../Components/Chart/useChart";
import { useDebounce, useMeasure } from "react-use";
import * as am4charts from "@amcharts/amcharts4/charts";
import { startCase, toLower } from "lodash-es";
import useWidgetFilterNavigator from "../../useWidgetFilterNavigator";
import {
  buildChartData,
  createCategoryAxis,
  createSeries,
  createValueAxis,
  type ChartDataPoint,
} from "../TableCharts/WidthSensetiveStackedBarChartUtils";
import type { ScopeSummaryItem } from "./useScopesGraph";

const getTicketColor = (type: string): string => {
  switch (type) {
    case "BACKLOG":
      return "#8DC9F3";
    case "SCHEDULED":
      return "#5565f9";
    case "IN_PROGRESS":
      return "#A673F1";
    case "REJECTED":
      return "#ec2a3f";
    default:
      return "#2dbf9f";
  }
};

export interface FindingsByTicketStatusProps {
  scopeData: ScopeSummaryItem["per_ticket_status"];
  max: number;
  scopeId: string | undefined;
  totalCount?: number;
  loading: boolean;
  setMax: Dispatch<SetStateAction<number>>;
  widgetId: string;
  filterToExecute?: string;
  isForExport?: boolean;
}

const FindingsByTicketStatus: React.FC<FindingsByTicketStatusProps> = ({
  scopeData,
  max,
  scopeId,
  totalCount,
  loading,
  setMax,
  widgetId,
  filterToExecute,
  isForExport,
}) => {
  const [dashboardData, setDashboardData] = useState<ChartDataPoint[] | null>(
    null
  );
  const [ref, { width: chartWidth }] = useMeasure<HTMLDivElement>();
  const [debouncedWidth, setDebouncedWidth] = useState(chartWidth);
  const chartName = `FindingsByTicketDiv-${scopeId}-${widgetId}`;

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: null,
    scopeId,
  });

  const {
    chartLoading,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(isForExport ? () => {} : null);

  useEffect(() => {
    if (!!loading) {
      startLoadingChart();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [loading]);

  useDebounce(
    () => {
      setDebouncedWidth(chartWidth);
    },
    1000,
    [chartWidth]
  );

  useEffect(() => {
    if (!!scopeData) {
      const { DELETED, ...scopeDataFiltered } = scopeData;
      const entries = Object.keys(scopeDataFiltered);

      const data = buildChartData(
        entries,
        entries.map((key) => scopeData[key]),
        totalCount ?? 0,
        max,
        // Preserves original `debouncedWidth || null`; buildChartData treats a
        // falsy width (null/0) identically, so behavior is unchanged.
        (debouncedWidth || null) as number,
        { scopeId: "" }
      );

      const total = entries.reduce(
        (sum, key) => sum + (scopeData[key] || 0),
        0
      );
      if (total > max) {
        setMax(total);
      }

      setDashboardData([data]);
      setChartError(false);
      stopLoadingChart();
      setChartDataExists(true);
    }
  }, [scopeData, debouncedWidth, max]);

  const getChart = (
    dashboardData: ChartDataPoint[]
  ): am4charts.XYChart | undefined => {
    if (dataExists) {
      let [chart] = createChart(chartName, am4charts.XYChart, {
        hideLegend: true,
      });
      chart.data = dashboardData;
      chart.paddingTop = isForExport ? 0 : 16;
      chart.paddingLeft = isForExport ? 0 : 6;
      chart.paddingRight = isForExport ? 0 : 6;

      let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
      createCategoryAxis(categoryAxis, "scopeId");
      let valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, max);

      const filteredKeys = Object.keys(dashboardData[0]).filter(
        (el) => el !== "scopeId" && !el.endsWith("Val")
      );

      filteredKeys.forEach((key) => {
        if (!dashboardData[0][key]) {
          return;
        }

        const series = createSeries(
          `${key}Val`,
          startCase(toLower(key)),
          dashboardData[0][key] as number,
          getTicketColor(key),
          "scopeId",
          !!isForExport,
          !!onFilterClick
        );

        series.columns.template.events.on("hit", () => {
          onFilterClick([["in", "remediation_item.ticket_status", key]]);
        });

        chart.series.push(series);
      });

      return chart;
    }
  };

  useLayoutEffect(() => {
    if (!!dashboardData) {
      const chart = getChart(dashboardData);

      return () => {
        chart?.dispose();
      };
    }
  }, [dashboardData, createChart, isForExport]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={false}
      chartData={dataExists}
      hideLegend
      chartDivClassName={"findingsByTicketDiv"}
      loaderType={loaderTypes.SKELETON}
      // useMeasure returns a callback ref; Chart types chartContainerRef as a
      // RefObject. Behavior is unchanged — cast bridges the ref-shape mismatch.
      chartContainerRef={ref as unknown as React.RefObject<HTMLDivElement>}
    />
  );
};

export default FindingsByTicketStatus;
